import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, animate, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../hooks/useSettings';

const staggerParent = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 }
};

const StatsCounter = ({ value }) => {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });

  const { target, suffix } = useMemo(() => {
    const numericMatch = String(value).match(/([\d.,]+)/);
    const numeric = numericMatch ? parseFloat(numericMatch[1].replace(/,/g, "")) : 0;
    const suffixPart = String(value).replace(/[\d.,]/g, "").trim();
    return { target: numeric, suffix: suffixPart };
  }, [value]);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration: 1.2,
      onUpdate: (latest) => {
        const formatted = new Intl.NumberFormat().format(Math.round(latest));
        setDisplay(`${formatted}${suffix}`);
      },
    });
    return () => controls.stop();
  }, [isInView, target, suffix]);

  return <span ref={ref}>{display}</span>;
};

const ParallaxHero = () => {
  const { theme } = useSettings();
  const { t } = useTranslation();
  const backgroundImage = theme?.coverImages?.hero || '/assets/images/hero-bg.jpg';
  const cardImage = theme?.coverImages?.heroCard || '/assets/images/sapiens.svg';
  const stats = [
    { label: t('hero.stats.students') || 'Students', value: '12k+' },
    { label: t('hero.stats.courses') || 'Courses', value: '240+' },
    { label: t('hero.stats.graduates') || 'Graduates', value: '5k+' },
  ];

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-safir-dark text-white min-h-screen flex items-center"
    >
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(115deg, rgba(14,23,39,0.9) 0%, rgba(14,23,39,0.75) 40%, rgba(30,64,175,0.45) 100%), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      {/* Soft glow accents */}
      <div className="absolute -left-20 top-10 w-72 h-72 bg-safir-primary/30 blur-3xl rounded-full" />
      <div className="absolute right-0 -bottom-10 w-80 h-80 bg-safir-secondary/25 blur-3xl rounded-full" />

      <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Copy */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerParent}
            className="space-y-6"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur">
              <span className="w-2 h-2 rounded-full bg-safir-secondary mr-2" />
              <span className="text-sm tracking-wide uppercase text-gray-200">
                {t('hero.chip')}
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
            >
              {t('hero.title')}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg sm:text-xl text-gray-100 leading-relaxed"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row gap-4 pt-2"
            >
              <a
                href="/courses"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-safir-primary to-safir-secondary font-semibold shadow-lg shadow-safir-primary/30 transition hover:-translate-y-0.5"
              >
                {t('hero.button')}
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/30 bg-white/10 backdrop-blur font-semibold text-white hover:bg-white/15 transition"
              >
                {t('hero.contactCta')}
              </a>
              <a
                href="/login"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-safir-primary/50 bg-safir-primary/20 backdrop-blur font-semibold text-white hover:bg-safir-primary/30 transition"
              >
                Login
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4"
            >
              {stats.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur"
                >
                  <div className="text-2xl font-bold">
                    <StatsCounter value={item.value} />
                  </div>
                  <div className="text-sm text-gray-200">{item.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-safir-primary/30 via-safir-secondary/20 to-transparent blur-2xl rounded-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
              <img
                src={cardImage}
                alt="Safir the Elite illustration"
                className="w-full h-full object-contain bg-gradient-to-br from-slate-900/20 to-white/5"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-md rounded-2xl px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-safir-primary/30 border border-white/10 flex items-center justify-center text-white font-semibold">
                  SE
                </div>
                <div>
                  <p className="text-sm text-gray-200">{t('hero.subtitle')}</p>
                  <p className="text-xs text-gray-300">{t('hero.tagline')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ParallaxHero;